import React from "react";
import { NavLink } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Sidebar navigation items.
 * - `NavLeaf` — single link (no children).
 * - `NavGroup` — collapsible parent with children.
 * - `NavSection` — picks the right variant based on the config.
 */

const ACTIVE_LINK_CLASSES = "bg-tralenex-50 text-tralenex-900";
const INACTIVE_LINK_CLASSES = "text-ink-primary hover:bg-inset";

export function NavSection({ section, collapsed, open, onToggle, currentPath }) {
  const hasChildren = !!section.children?.length;
  if (!hasChildren) {
    return <NavLeaf section={section} collapsed={collapsed} />;
  }
  return (
    <NavGroup
      section={section}
      collapsed={collapsed}
      open={open}
      onToggle={onToggle}
      currentPath={currentPath}
    />
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Leaf — single link
// ───────────────────────────────────────────────────────────────────────────
function NavLeaf({ section, collapsed }) {
  const Icon = section.icon;
  return (
    <NavLink
      to={section.to}
      end={section.to === "/dashboard"}
      className={({ isActive }) =>
        cn(
          "group relative flex items-center gap-3 px-3 mx-2 py-2 rounded-md text-sm font-medium transition-colors",
          isActive ? ACTIVE_LINK_CLASSES : INACTIVE_LINK_CLASSES,
          collapsed && "justify-center px-0",
        )
      }
      data-testid={`nav-link-${section.id}`}
    >
      {({ isActive }) => (
        <>
          {isActive && (
            <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-tralenex-600" />
          )}
          <Icon
            size={18}
            className={isActive ? "text-tralenex-700" : "text-ink-secondary group-hover:text-ink-primary"}
          />
          {!collapsed && <span className="flex-1">{section.label}</span>}
          {!collapsed && section.badge != null && <NavBadge value={section.badge} />}
        </>
      )}
    </NavLink>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Group — collapsible parent
// ───────────────────────────────────────────────────────────────────────────
function NavGroup({ section, collapsed, open, onToggle, currentPath }) {
  const Icon = section.icon;
  const isActiveParent = section.children.some((c) => currentPath.startsWith(c.to));

  return (
    <div className="px-2">
      <button
        type="button"
        onClick={onToggle}
        data-testid={`nav-group-${section.id}`}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors",
          isActiveParent ? "text-tralenex-900" : "text-ink-primary hover:bg-inset",
          collapsed && "justify-center px-0",
        )}
        aria-expanded={open}
      >
        <Icon
          size={18}
          className={isActiveParent ? "text-tralenex-700" : "text-ink-secondary"}
        />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{section.label}</span>
            {section.badge != null && <NavBadge value={section.badge} />}
            <ChevronDown
              size={14}
              className={cn(
                "text-ink-secondary transition-transform",
                open && "rotate-180",
              )}
            />
          </>
        )}
      </button>

      {!collapsed && open && (
        <ul className="mt-0.5 mb-1 pl-7 space-y-0.5">
          {section.children.map((child) => (
            <li key={child.id}>
              <NavChildLink parentId={section.id} child={child} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NavChildLink({ parentId, child }) {
  return (
    <NavLink
      to={child.to}
      className={({ isActive }) =>
        cn(
          "block px-3 py-1.5 rounded-md text-[13px] transition-colors",
          isActive
            ? "bg-tralenex-50 text-tralenex-900 font-semibold"
            : "text-ink-secondary hover:text-ink-primary hover:bg-inset",
        )
      }
      data-testid={`nav-link-${parentId}-${child.id}`}
    >
      {child.label}
    </NavLink>
  );
}

function NavBadge({ value }) {
  return (
    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-tralenex-600 text-white text-[10px] font-semibold">
      {value}
    </span>
  );
}
